import z from 'zod';

const landAreaFormValue = (initialAreaValue: number) => ({
  searchMode: 'exact' as const,
  landArea: initialAreaValue,
  landAreaMin: null as number | null,
  landAreaMax: null as number | null,
});

const nullablePositiveInteger = z.preprocess(
  (value) => (value === '' || value === null ? null : value),
  z.coerce.number().int().positive().nullable(),
);

const areaFormValidationSchema = z
  .object({
    searchMode: z.enum(['exact', 'range']),
    landArea: z.coerce
      .number({ invalid_type_error: 'La surface terrain doit être un nombre.' })
      .int('La surface terrain doit être un entier.')
      .positive('La surface terrain doit être strictement positive.'),
    landAreaMin: nullablePositiveInteger,
    landAreaMax: nullablePositiveInteger,
  })
  .superRefine((data, ctx) => {
    if (data.searchMode === 'exact') {
      return;
    }

    if (data.landAreaMin === null || data.landAreaMax === null) {
      const message =
        'Renseignez la surface mini ET maxi pour rechercher par plage.';
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message,
        path: ['landAreaMax'],
      });

      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message,
        path: ['landAreaMin'],
      });
      return;
    }

    const landAreaMin = data.landAreaMin;
    const landAreaMax = data.landAreaMax;

    if (landAreaMin > landAreaMax) {
      const message =
        'La surface mini ne peut pas être supérieure à la surface maxi.';
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message,
        path: ['landAreaMin'],
      });

      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message,
        path: ['landAreaMax'],
      });
      return;
    }
  });

const LetterListEnum = ['A', 'B', 'C', 'D', 'E', 'F', 'G'] as const;

const dpeFormValidationSchema = z
  .object({
    dpe: z
      .enum(['', ...LetterListEnum], {
        errorMap: () => ({ message: 'Veuillez sélectionner une classe DPE.' }),
      })
      .nullable(),
    ges: z
      .enum(['', ...LetterListEnum], {
        errorMap: () => ({ message: 'Veuillez sélectionner une classe GES.' }),
      })
      .nullable(),
    dpeDateStart: z
      .string()
      .nullable()
      .refine(
        (date) => {
          if (!date) return false;
          return new Date(date) <= new Date();
        },
        {
          message: 'La date de début ne peut pas être dans le futur.',
        },
      ),
    dpeDateEnd: z
      .string()
      .nullable()
      .refine(
        (date) => {
          if (!date) return false;
          return new Date(date) <= new Date();
        },
        {
          message: 'La date de fin ne peut pas être dans le futur.',
        },
      ),
    landArea: z.coerce.number().nullable(),
    builtAreaMin: z.coerce.number().nullable(),
    builtAreaMax: z.coerce.number().nullable(),
    typology: z.string(),
  })
  .refine(
    (data) => {
      if (data.builtAreaMin !== null && data.builtAreaMax !== null) {
        return data.builtAreaMin <= data.builtAreaMax;
      }
      return true; // no control if only one value is null :)
    },
    {
      message:
        'La surface minimum ne peut pas être supérieure à la surface maximum.',
      path: ['builtAreaMax'], // if error, set error to specified field
    },
  )
  .refine(
    (data) => {
      if (data.dpeDateStart && data.dpeDateEnd) {
        return new Date(data.dpeDateStart) <= new Date(data.dpeDateEnd);
      }
      return true;
    },
    {
      message: 'La date de début DPE ne peut pas dépasser la date de fin.',
      path: ['dpeDateEnd'],
    },
  )
  .refine(
    (data) => {
      // At least one of dpe or ges must be provided with a valid value from LetterListEnum
      const isDpeValid =
        data.dpe !== null &&
        data.dpe !== '' &&
        LetterListEnum.includes(data.dpe as any);
      const isGesValid =
        data.ges !== null &&
        data.ges !== '' &&
        LetterListEnum.includes(data.ges as any);
      return isDpeValid || isGesValid;
    },
    {
      message: 'Veuillez sélectionner au moins une classe DPE ou GES.',
      path: ['dpe'], // Show error on dpe field
    },
  );

export type LandAreaFormValuesType = z.infer<typeof areaFormValidationSchema>;
export type DpeFormValuesType = z.infer<typeof dpeFormValidationSchema>;

export {
  areaFormValidationSchema,
  dpeFormValidationSchema,
  landAreaFormValue,
  LetterListEnum,
};
